import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Paperclip, Leaf, Edit3, Mic, Play, Pause } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import VoiceButton from "@/components/VoiceButton";

const ChatInterface = ({ messages, onSendMessage, onUploadImage, isLoading, user, onOpenPremiumPlans }) => {
  const [input, setInput] = useState("");
  const [isVoiceMode, setIsVoiceMode] = useState(true);
  const [isVoiceDraft, setIsVoiceDraft] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [playingIdx, setPlayingIdx] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const inputRef = useRef(null);
  const blockedToastShownRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-play AI voice responses — only when NOT muted
  useEffect(() => {
    if (!isVoiceMode) return;
    const lastIdx = messages.length - 1;
    const lastMessage = messages[lastIdx];
    if (lastMessage && lastMessage.role === 'assistant' && lastMessage.audio_url) {
      playAudio(lastMessage.audio_url, lastIdx);
    }
  }, [messages, isVoiceMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mute toggle → immediately stop any ongoing AI playback
  useEffect(() => {
    if (!isVoiceMode) {
      stopAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoiceMode]);

  const playAudio = (audioUrl, idx = null) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = audioUrl;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          setAutoplayBlocked(false);
          if (idx !== null) setPlayingIdx(idx);
        })
        .catch((err) => {
          // NotAllowedError = browser blocked autoplay (no recent user gesture)
          // Surface a one-time toast + show inline play buttons on AI messages
          if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
            setAutoplayBlocked(true);
            if (!blockedToastShownRef.current) {
              blockedToastShownRef.current = true;
              toast.info("Tap the ▶︎ button on any reply to hear it — your browser blocked auto-play.", { duration: 7000 });
            }
          } else {
            console.error('Audio playback error:', err);
          }
        });
    }
  };

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingIdx(null);
  }, []);

  const togglePlayMessage = (idx, audioUrl) => {
    if (playingIdx === idx) {
      stopAudio();
    } else {
      // Any direct user click also unblocks autoplay for future messages
      blockedToastShownRef.current = false;
      setAutoplayBlocked(false);
      playAudio(audioUrl, idx);
    }
  };

  // Keep playingIdx accurate when audio finishes naturally
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setPlayingIdx(null);
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSendMessage(input.trim(), isVoiceMode);
      setInput("");
      setIsVoiceDraft(false);
    }
  };

  // Voice transcript handler — places text in input for editing, does NOT auto-send
  const handleVoiceTranscript = useCallback((transcript, isFinal) => {
    if (!transcript) return;
    if (isFinal) {
      // Recording stopped — place full text in input for review/edit
      setInput(transcript);
      setIsVoiceDraft(true);
      // Focus the input so user can immediately edit
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      // Live preview while still recording — update input in real time
      setInput(transcript);
    }
  }, []);

  const handleRecordingStart = useCallback(() => {
    stopAudio();
    setInput("");
    setIsVoiceDraft(false);
  }, [stopAudio]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUploadImage(file);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#F8F6F1] relative overflow-hidden">
      {/* Subtle decorative shape — moved to bottom-right only so it does not obscure the chat header */}
      <div className="pointer-events-none absolute bottom-0 right-0 w-48 h-48 bg-[#E5D4C1]/30 organic-blob translate-x-1/4 translate-y-1/4" />
      
      {/* Header — chat-only context (no duplicate branding; brand lives in the top nav) */}
      <div className="relative z-10 px-5 py-3 border-b border-[#D4C9B8]/40 bg-white/90 backdrop-blur-sm flex-shrink-0" data-testid="chat-header">
        <div className="flex items-center gap-2">
          <span className="font-sans text-[11px] uppercase tracking-[0.18em] text-[#7A9B7E]">Chronicle</span>
          <span className="font-serif text-sm text-[#4A5D4A]">— your AI memoir companion</span>
        </div>
      </div>

      {/* Messages */}
      <div className="relative z-10 flex-1 overflow-y-auto p-5 space-y-5" data-testid="chat-messages-container">
        {messages.map((msg, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div className="flex gap-3 max-w-[85%]">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center flex-shrink-0 mt-1">
                  <Leaf className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <div
                    className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 shadow-sm rounded-3xl rounded-tl-sm p-4 text-[#4A5D4A] font-sans text-base leading-relaxed"
                    data-testid="ai-message"
                  >
                    {msg.content}
                  </div>
                  {msg.audio_url && (
                    <button
                      type="button"
                      onClick={() => togglePlayMessage(index, msg.audio_url)}
                      className={`mt-1.5 ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-sans transition-all ${
                        playingIdx === index
                          ? "bg-[#7A9B7E] text-white shadow-sm"
                          : autoplayBlocked
                            ? "bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 animate-pulse"
                            : "bg-[#E5D4C1]/50 text-[#7A8A7A] hover:bg-[#E5D4C1]"
                      }`}
                      title={playingIdx === index ? "Pause" : "Play audio"}
                      data-testid={`play-audio-${index}`}
                    >
                      {playingIdx === index ? (
                        <><Pause className="w-3 h-3" /> Playing…</>
                      ) : (
                        <><Play className="w-3 h-3" /> Play</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
            {msg.role === 'user' && (
              <div 
                className="bg-gradient-to-br from-[#7A9B7E] to-[#6A8B6E] text-white rounded-3xl rounded-tr-sm px-5 py-3 shadow-md max-w-[85%]"
                data-testid="user-message"
              >
                <p className="font-sans leading-relaxed">{msg.content}</p>
              </div>
            )}
          </motion.div>
        ))}
        
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-3"
            data-testid="typing-indicator"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center flex-shrink-0">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <div className="flex gap-1 items-center py-2">
              <div className="w-2 h-2 bg-[#7A9B7E] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-[#7A9B7E] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-[#7A9B7E] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="relative z-10 p-4 bg-[#F8F6F1] border-t border-[#D4C9B8]/30">
        {/* Voice draft indicator */}
        <AnimatePresence>
          {isVoiceDraft && input.trim() && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-[#9FB89F]/15 border border-[#9FB89F]/30 rounded-2xl"
            >
              <Edit3 className="w-3.5 h-3.5 text-[#7A9B7E] flex-shrink-0" />
              <span className="font-sans text-xs text-[#5A7B5E]">
                Voice captured — edit below if needed, then hit send
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Text input */}
        <form onSubmit={handleSubmit} className="relative mb-3">
          <div className={`flex items-end gap-2 bg-white/60 backdrop-blur-sm border rounded-3xl shadow-sm px-4 py-2.5 transition-colors ${
            isVoiceDraft ? 'border-[#9FB89F]/60' : 'border-[#D4C9B8]/50'
          }`}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); if (!e.target.value.trim()) setIsVoiceDraft(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
              placeholder="Type or tap the mic to speak..."
              className="flex-1 bg-transparent outline-none font-sans text-sm text-[#4A5D4A] placeholder:text-[#9A9A8A] resize-none min-h-[20px] max-h-[120px] leading-relaxed"
              disabled={isLoading}
              rows={Math.min(Math.max(Math.ceil(input.length / 50), 1), 5)}
              data-testid="chat-input-field"
            />
            <div className="flex items-center gap-1.5 flex-shrink-0 pb-0.5">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-1.5 hover:bg-[#E5D4C1]/30 rounded-lg transition-colors"
                data-testid="photo-upload-button"
              >
                <Paperclip className="w-4 h-4 text-[#8A7A6A]" />
              </button>
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="bg-[#7A9B7E] text-white p-2 rounded-xl hover:bg-[#6A8B6E] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                data-testid="chat-send-button"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </form>
        
        {/* Voice Buttons row — standard + premium multi-language placeholder */}
        <div className="flex flex-col items-center gap-3">
          <VoiceButton 
            onTranscript={handleVoiceTranscript}
            isLoading={isLoading}
            onRecordingStart={handleRecordingStart}
            voiceMode={isVoiceMode}
            onToggleVoiceMode={() => setIsVoiceMode(!isVoiceMode)}
          />

          {/* Premium "second mic" — Whisper-1 multi-language voice */}
          <button
            type="button"
            onClick={() => onOpenPremiumPlans?.()}
            disabled={isLoading}
            className="group relative flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-gradient-to-r from-[#1a1a2e] via-[#2D3A2D] to-[#1a1a2e] hover:from-[#2D3A2D] hover:to-[#3a4d3a] shadow-md hover:shadow-lg hover:shadow-amber-300/20 transition-all disabled:opacity-50 overflow-hidden"
            data-testid="premium-mic-button"
            title="Premium voice — Hindi, Tamil, Hinglish & 50+ languages"
          >
            {/* Subtle animated shine for the premium feel */}
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

            {/* Mic icon in a gold ring */}
            <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-inner ring-2 ring-amber-200/30">
              <Mic className="w-4 h-4 text-[#2D3A2D]" strokeWidth={2.5} />
            </span>

            {/* Label */}
            <span className="relative flex flex-col items-start leading-tight">
              <span className="font-serif text-[12px] text-amber-200 tracking-wide flex items-center gap-1">
                Premium Voice
                <span className="text-[8px] uppercase tracking-[0.18em] bg-amber-300/15 text-amber-300 px-1.5 py-[1px] rounded-full border border-amber-300/30">PRO</span>
              </span>
              <span className="font-sans text-[9px] text-amber-100/70 tracking-wider">
                50+ languages · Hinglish ready
              </span>
            </span>
          </button>
        </div>
      </div>

      <audio
        ref={audioRef}
        preload="auto"
        playsInline
        className="sr-only"
        aria-hidden="true"
      />
    </div>
  );
};

export default ChatInterface;
